import { Alert, Button, Checkbox, Empty, Modal, Space, Spin, Tag, notification } from 'antd';
import { useEffect, useState } from 'react';
import CustomButton from 'components/custom-button';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import {
  DEPARTMENT_LABEL,
  MISSION_PERIOD_LABEL,
  STAR_GROUP_LABEL,
  TEMPLATE_GROUP_LABEL,
  labelOf,
} from 'pages/star-shared/labels';
import { mapStarError } from 'pages/star-shared/star-error';
import {
  MissionTemplate,
  MissionTemplateCreateResult,
  MissionTemplateGroup,
} from 'pages/star-shared/types';
import { extractArray, formatNumber } from 'pages/star-shared/utils';
import { colors } from 'theme/tokens';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

const GROUPS: MissionTemplateGroup[] = ['STAFF', 'CREATOR_STUDIO'];

const rewardText = (t: MissionTemplate) => {
  const period = labelOf(MISSION_PERIOD_LABEL, t.period).toLowerCase();
  const max = t.maxRewardsPerPeriod ? ` · tối đa ${t.maxRewardsPerPeriod} lần/${period}` : '';
  // Giới hạn LẦN NỘP /ngày, /tuần của mẫu (doc 10 §3); máy chủ cũ không trả → không nối.
  const limits = [
    t.maxPerDay ? `${t.maxPerDay}/ngày` : '',
    t.maxPerWeek ? `${t.maxPerWeek}/tuần` : '',
  ]
    .filter(Boolean)
    .join(' · ');
  const limitText = limits ? ` · tối đa ${limits}` : '';
  return `${formatNumber(t.rewardStars)} ⭐/lần${max}${limitText}`;
};

const AudienceTags = ({ t }: { t: MissionTemplate }) => {
  const groups = t.audience?.starGroups || [];
  const departments = t.audience?.departments || [];
  return (
    <Space size={4} wrap>
      {groups.map((g) => (
        <Tag key={g} color="blue">
          {labelOf(STAR_GROUP_LABEL, g)}
        </Tag>
      ))}
      {departments.map((d) => (
        <Tag key={d}>{DEPARTMENT_LABEL[d] || d}</Tag>
      ))}
    </Space>
  );
};

// "Tạo từ mẫu": GET/POST /stars/admin/missions/templates (SPEC-NHIEM-VU-MO-RONG.md §6).
// Mẫu tạo ở trạng thái Nháp; mẫu đã có (trùng mã) hiện "Đã có" và không chọn được.
const TemplateModal = ({ open, onClose, onCreated }: Props) => {
  const [templates, setTemplates] = useState<MissionTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setSelected([]);
    setLoading(true);
    AxiosUtils.get(API.starsMissionTemplates)
      .then((res: any) => {
        if (!cancelled) setTemplates(extractArray<MissionTemplate>(res));
      })
      .catch((err: any) => {
        if (!cancelled) notification.error({ message: mapStarError(err) });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const toggle = (code: string, checked: boolean) =>
    setSelected((prev) => (checked ? [...prev, code] : prev.filter((c) => c !== code)));

  const selectGroup = (group: MissionTemplateGroup) => {
    const codes = templates.filter((t) => t.group === group && !t.exists).map((t) => t.code);
    setSelected((prev) => Array.from(new Set([...prev, ...codes])));
  };

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const submit = async () => {
    if (!selected.length) return;
    setSaving(true);
    try {
      const res: any = await AxiosUtils.post(API.starsMissionTemplates, { codes: selected });
      const data: MissionTemplateCreateResult = res?.data || { created: [], skipped: [] };
      const created = data.created?.length || 0;
      const skipped = data.skipped || [];
      notification.success({
        message: created
          ? `Đã tạo ${created} nhiệm vụ ở trạng thái Nháp.`
          : 'Không tạo thêm nhiệm vụ nào.',
        description: [
          created ? 'Đọc lại nội dung rồi đổi trạng thái sang "Đang chạy" để bật.' : '',
          skipped.length ? `Bỏ qua ${skipped.length} mẫu đã có: ${skipped.join(', ')}.` : '',
        ]
          .filter(Boolean)
          .join(' '),
        duration: 8,
      });
      onCreated();
      onClose();
    } catch (err) {
      notification.error({ message: mapStarError(err) });
    } finally {
      setSaving(false);
    }
  };

  const renderGroup = (group: MissionTemplateGroup) => {
    const list = templates.filter((t) => t.group === group);
    if (!list.length) return null;
    const free = list.filter((t) => !t.exists && !selected.includes(t.code)).length;
    return (
      <div key={group} style={{ marginBottom: 16 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 8,
          }}
        >
          <strong style={{ fontSize: 15 }}>{TEMPLATE_GROUP_LABEL[group]}</strong>
          <Button size="small" disabled={!free} onClick={() => selectGroup(group)}>
            Chọn tất cả chưa có ({free})
          </Button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {list.map((t) => {
            const checked = selected.includes(t.code);
            return (
              // Bấm cả thẻ để chọn; ô tick chỉ hiển thị (sự kiện bấm nổi lên thẻ).
              <div
                key={t.code}
                role="checkbox"
                aria-checked={checked}
                aria-disabled={t.exists}
                tabIndex={t.exists ? -1 : 0}
                onClick={() => !t.exists && toggle(t.code, !checked)}
                onKeyDown={(e) => {
                  if (!t.exists && (e.key === ' ' || e.key === 'Enter')) {
                    e.preventDefault();
                    toggle(t.code, !checked);
                  }
                }}
                style={{
                  display: 'flex',
                  gap: 12,
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: `1px solid ${checked ? colors.accentLine : colors.line}`,
                  background: checked ? colors.accentSoft : colors.surface,
                  cursor: t.exists ? 'not-allowed' : 'pointer',
                  opacity: t.exists ? 0.7 : 1,
                }}
              >
                <Checkbox
                  checked={checked}
                  disabled={t.exists}
                  tabIndex={-1}
                  style={{ marginTop: 2, pointerEvents: 'none' }}
                />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <strong>{t.title}</strong>
                    {t.exists && <Tag color="green">Đã có</Tag>}
                  </div>
                  {t.description && (
                    <div style={{ color: colors.textSecondary, fontSize: 13, marginTop: 2 }}>
                      {t.description}
                    </div>
                  )}
                  <div
                    style={{
                      display: 'flex',
                      gap: 8,
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      marginTop: 6,
                    }}
                  >
                    <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{rewardText(t)}</span>
                    <AudienceTags t={t} />
                    <code style={{ color: colors.textSecondary, fontSize: 12 }}>{t.code}</code>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <Modal
      title="Tạo nhiệm vụ từ mẫu"
      open={open}
      onCancel={handleClose}
      footer={null}
      destroyOnHidden
      maskClosable={false}
      width={720}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Mẫu được tạo ở trạng thái Nháp để bạn đọc lại rồi mới bật. Mọi mẫu đều cần duyệt báo cáo: người làm gửi minh chứng, Quản lý hoặc Admin duyệt."
      />
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : templates.length ? (
        <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: 4 }}>
          {GROUPS.map(renderGroup)}
        </div>
      ) : (
        <Empty description="Chưa tải được danh sách mẫu." />
      )}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 8,
          marginTop: 16,
        }}
      >
        <span style={{ color: colors.textSecondary }}>
          {selected.length ? `Đã chọn ${selected.length} mẫu` : 'Chưa chọn mẫu nào'}
        </span>
        <div style={{ display: 'flex', gap: 8 }}>
          <CustomButton text="Đóng" bgColor="primary-grey" onClick={handleClose} />
          <CustomButton
            text={selected.length ? `Tạo ${selected.length} nhiệm vụ (Nháp)` : 'Tạo nhiệm vụ'}
            bgColor="secondary-green"
            loading={saving}
            disabled={!selected.length}
            onClick={submit}
          />
        </div>
      </div>
    </Modal>
  );
};

export default TemplateModal;
